import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthProvider";

import { Link, useNavigate } from "react-router-dom";
import axios from "../api/axios"; // ✅ Ensure axios import
import './styles/LoginSignup.css';
import config from "../config/config";
import SEO from "../seo/SEO";

const Login = () => {
  const { login, auth, loading: authLoading } = useAuth();
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [userId, setUserId] = useState(null); // 🔹 Store userId for OTP verification
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAMethod, setTwoFAMethod] = useState(null); // 🔹 "email" or "app"
  const [error, setError] = useState("");
  const [otpDigits, setOtpDigits] = useState(["", "", "", "", "", ""]);
  const otp = otpDigits.join(""); // Combine to send to backend

  const [loginLoading, setLoginLoading] = useState(false);
  const [verifyingOTP, setVerifyingOTP] = useState(false);
  const [availableEmails, setAvailableEmails] = useState([]);
  const [selectedDeliveryEmail, setSelectedDeliveryEmail] = useState(null);
  

  const [usingBackupCode, setUsingBackupCode] = useState(false);
  const [backupCode, setBackupCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const [codeSent, setCodeSent] = useState(false);

  const sendCodeToSelectedEmail = async () => {
    try {
      const csrfToken = document.cookie
        .split("; ")
        .find(row => row.startsWith("csrfToken="))
        ?.split("=")[1];
  
      if (!csrfToken) {
        alert("Missing CSRF token.");
        return;
      }
  
      await axios.post("/api/auth/send-login-code-to-email", {
        email: selectedDeliveryEmail,
        userId,
      }, {
        headers: { "x-csrf-token": csrfToken },
        withCredentials: true
      });
  
      setCodeSent(true);
      setOtpDigits(["", "", "", "", "", ""]); // clear OTP
      alert(`Code sent to ${selectedDeliveryEmail}`);
    } catch (err) {
      console.error("Failed to send code to selected email:", err);
      alert("Failed to send code. Try again.");
    }
  };
  

  const navigate = useNavigate();

  useEffect(() => {
    if (auth.isAuthenticated) {
      if (["admin", "siteadmin"].includes(auth.user?.role)) {
        navigate("/admin/dashboard");
      } else {
        navigate("/user/profile");
      }
    }
  }, [auth, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return; // Only digits, one character max
    const updatedDigits = [...otpDigits];
    updatedDigits[index] = value;
    setOtpDigits(updatedDigits);
  
    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`).focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
  
    if (pasteData.length === 6) {
      const updatedDigits = pasteData.split('');
      setOtpDigits(updatedDigits);
  
      // Optionally, focus the last input
      document.getElementById('otp-5').focus();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otpDigits[index] && index > 0) {
      const updatedDigits = [...otpDigits];
      updatedDigits[index - 1] = "";
      setOtpDigits(updatedDigits);
      document.getElementById(`otp-${index - 1}`).focus();
    }
  };
  
  const resendVerificationEmail = async (email) => {
    try {
      await axios.post("/api/auth/send-verification-email", { email });
      alert("Verification email has been resent!");
    } catch (err) {
      alert("Failed to resend verification email. Please try again later.");
    }
  };
  
  

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoginLoading(true);
  
    try {
      const response = await login(formData);
  
      if (response?.twofactorRequired) {
        setUserId(response.userId);
        setTwoFARequired(true);
        setTwoFAMethod(response.twoFactorMethod);
      
        if (response.twoFactorMethod === "email") {
          // Wait one tick before fetching to allow state/render to catch up
          setTimeout(async () => {
            try {
              const { data } = await axios.get("/api/auth/backup-emails-for-login", {
                params: { email: formData.email },
              });
              
      
              const verified = data.emails?.filter(e => e.verified) || [];
              const primary = formData.email.toLowerCase();
      
              const allEmails = [
                { email: primary, label: `${primary} (Default)` },
                ...verified
                  .filter(e => e.full !== primary)
                  .map(e => ({ email: e.full, label: e.email })) // email = obfuscated, full = actual
              ];
              
              console.log(allEmails)
              setAvailableEmails(allEmails);
              setSelectedDeliveryEmail(primary);
            } catch (err) {
              console.error("❌ Failed to fetch backup emails:", err);
            }
          }, 0);
        }
      } else if (response.success) {
        // ✅ AuthProvider.js handles redirection
      } else {
        if (response.error === "Please verify your email before logging in.") {
          setError(
            <>
              {response.error}{" "}
              <button
                type="button"
                className="resend-link"
                onClick={() => resendVerificationEmail(formData.email)}
              >
                Resend verification email
              </button>
            </>
          );
        } else {
          setError(response.error);
        }
        setTwoFARequired(false);
      }
    } catch (err) {
      setError("Login failed. Please try again.");
      setTwoFARequired(false);
    } finally {
      setLoginLoading(false);
    }
  };



  const verifyOTP = async (e) => {
    e.preventDefault();
    setError("");
    setVerifyingOTP(true);
  
    try {
      const payload = usingBackupCode
      ? { userId, backupCode }
      : { userId, otp, email: selectedDeliveryEmail }; // include the chosen email
    
  
      const res = await axios.post("/api/auth/verify-login-2fa", payload);
  
      if (res.data.user) {
        await axios.get("/api/auth/me");
        
        // ✅ Redirect based on role
        if (["admin", "siteadmin"].includes(res.data.user.role)) {
          window.location.href = "/admin/dashboard";
        } else {
          window.location.href = "/user/profile";
        }
  
      } else {
        setError(res.data.error || "Invalid code.");
      }
    } catch (err) {
      setError("Verification failed.");
    } finally {
      setVerifyingOTP(false);
    }
  };
  
  
  if (authLoading) {
    return (
      <div className="auth-page-wrapper">
        <div className="auth-container">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }
  

  return (
    <>
          <SEO
              robots="noindex, follow"
              title="Login - Withrow Knives"
              description="Log in to your Withrow Knives account to manage your orders, update profile details, and enhance security with two-factor authentication."
              keywords={[
                  "Withrow Knives login",
                  "account sign in",
                  "user authentication",
                  "secure login",
                  "two-factor authentication"
              ]}
              url={`${config.metadata.siteUrl}/login`}
              jsonLd={{
                  "@context": "https://schema.org",
                  "@type": "WebPage",
                  "name": "Login - Withrow Knives",
                  "url": `${config.metadata.siteUrl}/login`,
                  "description": "Log in to your Withrow Knives account to manage your orders, update profile details, and enhance security with two-factor authentication.",
                  "breadcrumb": {
                      "@type": "BreadcrumbList",
                      "itemListElement": [
                          {
                              "@type": "ListItem",
                              "position": 1,
                              "name": "Home",
                              "item": config.metadata.siteUrl
                          },
                          {
                              "@type": "ListItem",
                              "position": 2,
                              "name": "Login",
                              "item": `${config.metadata.siteUrl}/login`
                          }
                      ]
                  },
                  "mainEntityOfPage": {
                      "@type": "WebPage",
                      "@id": `${config.metadata.siteUrl}/login`
                  }
              }}
          />


      <div className="auth-page-wrapper">
        <div className="auth-container">
          <h2>Login</h2>
          <p>Don't have an account? <Link to="/signup">Sign Up</Link></p>
          {error && <p className="error">{error}</p>}

          {!twoFARequired ? (
            <form onSubmit={handleLogin}>
              <label htmlFor="login-email" className="sr-only">Email</label>
              <input 
                id="login-email"
                type="email" 
                name="email" 
                placeholder="Email" 
                onChange={handleChange} 
                required 
              />

              <div className="password-wrapper">
                <label htmlFor="login-password" className="sr-only">Password</label>
                <input
                  id="login-password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  placeholder="Password"
                  onChange={handleChange}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword((prev) => !prev)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={`fa-solid ${showPassword ? "fa-eye-slash" : "fa-eye"}`}></i>
                </button>
              </div>

              <button type="submit" disabled={loginLoading}>
                {loginLoading ? <div className="spinner"></div> : "Login"}
              </button>
            </form>
          ) : (
            <form onSubmit={verifyOTP}>
                {/* ✅ Show resend dropdown for email 2FA if backup emails exist */}
                {twoFAMethod === "email" && availableEmails.length > 1 && (
                  <div className="email-select-container">
                    <label htmlFor="deliveryEmail">Didn't receive the code? Send it to a backup email:</label>
                    <select
                      id="deliveryEmail"
                      value={selectedDeliveryEmail}
                      onChange={(e) => setSelectedDeliveryEmail(e.target.value)}
                    >
                      {availableEmails.map((entry, i) => (
                        <option key={i} value={entry.email}>
                          {entry.label}
                        </option>
                      ))}
                    </select>
                    <button type="button" onClick={sendCodeToSelectedEmail}>
                      Send Code
                    </button>
                  </div>
                )}
              <p className="info">
                {twoFAMethod === "email"
                  ? "Enter the 6-digit code sent to your email."
                  : usingBackupCode
                  ? "Enter one of your backup codes."
                  : "Enter the 6-digit code from your authenticator app."}
              </p>
                {/* ✅ Show current target email where the user should check for the OTP */}
                {twoFAMethod === "email" && selectedDeliveryEmail && (
                  <p className="info">
                    Check your inbox: <strong>{selectedDeliveryEmail}</strong>
                  </p>
                )}

              {!usingBackupCode ? (
                <>
                  <div className="otp-container" onPaste={handleOtpPaste}>
                    {otpDigits.map((digit, index) => (
                      <input
                        key={index}
                        id={`otp-${index}`}
                        type="text"
                        inputMode="numeric"
                        maxLength="1"
                        value={digit}
                        onChange={(e) => handleOtpChange(e.target.value, index)}
                        onKeyDown={(e) => handleOtpKeyDown(e, index)}
                        className="otp-box"
                        aria-label={`Enter OTP digit ${index + 1} of 6`}
                        placeholder=""
                      />
                    ))}
                  </div>

                  {twoFAMethod === "app" && !usingBackupCode && (
                    <button
                      type="button"
                      className="switch-method-button"
                      onClick={() => {
                        setUsingBackupCode(true);
                        setOtpDigits(["", "", "", "", "", ""]); // Clear OTP
                      }}
                      aria-label="Lost access? Switch to backup code authentication"
                    >
                      Lost Access? Enter a Backup Code Instead
                    </button>
                  )}
                </>
              ) : (
                <>
                  <label htmlFor="backup-code-input" className="sr-only">Enter backup code</label>
                  <input
                    id="backup-code-input"
                    type="text"
                    placeholder="Enter your backup code"
                    value={backupCode}
                    onChange={(e) => setBackupCode(e.target.value)}
                    className="backup-code-input"
                    required
                  />

                  {twoFAMethod === "app" && usingBackupCode && (
                    <button
                      type="button"
                      className="switch-method-button"
                      onClick={() => {
                        setUsingBackupCode(false);
                        setBackupCode(""); // Clear backup code
                      }}
                      aria-label="Switch to entering a 6-digit OTP code"
                    >
                      Use 6-Digit Authenticator Code
                    </button>
                  )}
                </>
              )}

              <button
                type="submit"
                disabled={
                  verifyingOTP ||
                  (usingBackupCode ? backupCode.length < 8 : otp.length !== 6)
                }
                aria-label={`Verify ${usingBackupCode ? "Backup Code" : "OTP Code"}`}
              >
                {verifyingOTP ? <div className="spinner"></div> : `Verify ${usingBackupCode ? "Backup Code" : "OTP"}`}
              </button>
            </form>
          )}

          {/* Forgot Password Link */}
          <p>
            <Link to="/forgot-password">Forgot Password?</Link>
          </p>
        </div>
      </div>

    </>
  );
};

export default Login;
